import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useReferral } from '../../contexts/referral.context';
import { useGetReferralLinkMutation, useCleanupReferralFingerprintMutation } from '@/store/api';
import { updateSessionAction } from '../../store/slices/auth/actions/update-session.action';
import {
	buildLobbyDetailsUrl,
	collectFingerprint,
	getPublicIpAddress,
	removeFromStorage,
} from '@/utils';
import { ReferralLinkResponse } from '@sportspredict/shared';
import { ROUTES, HAS_SESSION_KEY } from '@/constants';

interface ReferralData {
	referralCode: string | null;
	ipAddress: string | undefined;
	fingerprint: ReturnType<typeof collectFingerprint> | undefined;
	data: ReferralLinkResponse | null;
}

interface PostAuthParams {
	accessToken?: string;
	referralCode: string | null;
	data: ReferralLinkResponse | null;
	fingerprint?: ReturnType<typeof collectFingerprint>;
	ipAddress?: string;
	redirectUrl?: string | null;
}

export function usePostAuthFlow() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const { getReferralCode } = useReferral();
	const [getReferralLink] = useGetReferralLinkMutation();
	const [cleanupFingerprint] = useCleanupReferralFingerprintMutation();

	const collectReferralData = useCallback(async (): Promise<ReferralData> => {
		const referralCode = await getReferralCode();
		const ipAddress = referralCode ? await getPublicIpAddress() : undefined;
		const fingerprint = referralCode ? collectFingerprint() : undefined;
		const data = referralCode ? await getReferralLink({ referralCode }).unwrap() : null;
		return { referralCode, ipAddress, fingerprint, data };
	}, [getReferralCode, getReferralLink]);

	const handlePostAuth = useCallback(
		async ({
			accessToken,
			referralCode,
			data,
			fingerprint,
			ipAddress,
			redirectUrl,
		}: PostAuthParams) => {
			localStorage.setItem(HAS_SESSION_KEY, 'true');

			if (referralCode && accessToken) {
				const fp = fingerprint ?? collectFingerprint();
				const ip = ipAddress ?? (await getPublicIpAddress());
				await cleanupFingerprint({ referralCode, fingerprint: fp, ipAddress: ip }).unwrap();
				removeFromStorage();
			}
			// updateSessionAction is defined in a JS file without type annotations
			(dispatch as any)(updateSessionAction({ accessToken } as any));
			if (data?.lobbyId && data?.eventId) {
				navigate(buildLobbyDetailsUrl(data.lobbyId, data.eventId));
			} else {
				const redirect = redirectUrl ?? searchParams.get('redirect');
				if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
					// A full page load here races the router's route prefetch: WebKit cancels the
					// in-flight chunk import, React Router reloads the page, and the user is left on /auth.
					navigate(redirect, { replace: true });
				} else if (redirect) {
					window.location.replace(redirect);
				} else if (location.pathname !== ROUTES.AUTH && location.pathname !== ROUTES.ROOT) {
					navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true });
				} else {
					navigate(ROUTES.HOME);
				}
			}
		},
		[dispatch, navigate, searchParams, cleanupFingerprint, location]
	);

	return { collectReferralData, handlePostAuth };
}
